import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../utils/api';
import { Printer, ArrowLeft } from 'lucide-react';

/** Printable A/B/C/D bubble answer sheet for a marketplace purchase.
 *  The QR code deep-links the buyer straight into the photo-upload flow so a
 *  parent can print, fill, snap, and auto-grade with a single tap. */
const MarketplacePrintPage = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const [purchase, setPurchase] = useState(null);
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const mine = await api.marketplaceMyPurchases();
        const p = mine.purchases.find((x) => x.id === purchaseId);
        if (!p) { navigate('/marketplace'); return; }
        setPurchase(p);
        setWorksheet(mine.worksheets[p.worksheet_id]);
      } finally {
        setLoading(false);
      }
    })();
  }, [purchaseId, navigate]);

  if (loading || !worksheet) {
    return <div className="min-h-screen flex items-center justify-center bg-white text-slate-500">Loading answer sheet…</div>;
  }

  const letters = ['A', 'B', 'C', 'D'];
  const total = worksheet.num_questions || 0;
  const uploadUrl = `${window.location.origin}/marketplace/grade/${purchaseId}?upload=1`;
  // 2-column layout: split questions down the middle. Ceil so left column is
  // always ≥ right column (natural read order).
  const half = Math.ceil(total / 2);
  const rows = Array.from({ length: half }, (_, i) => ({ left: i + 1, right: i + 1 + half }));

  return (
    <div className="mkt-print-root">
      <div className="mkt-print-toolbar no-print">
        <button onClick={() => navigate(`/marketplace/grade/${purchaseId}`)}
          className="flex items-center gap-1.5 text-slate-600 hover:text-amber-700 font-semibold text-sm" data-testid="mkt-print-back">
          <ArrowLeft size={16} /> Back to grading
        </button>
        <button onClick={() => window.print()}
          className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg px-4 py-2 text-sm" data-testid="mkt-print-btn">
          <Printer size={16} /> Print
        </button>
      </div>

      <div className="mkt-sheet">
        <div className="mkt-header">
          <div className="mkt-brand">
            <div className="mkt-brand-tag">Prof.Scaly · Tutors' Treasure Box</div>
            <div className="mkt-title">{worksheet.title}</div>
            <div className="mkt-sub">by {worksheet.tutor_name} · {worksheet.level} · {total} questions</div>
          </div>
          <div className="mkt-qr" data-testid="mkt-print-qr">
            <QRCodeSVG value={uploadUrl} size={100} level="M" includeMargin={false} />
            <div className="mkt-qr-caption">Scan to upload photo</div>
          </div>
        </div>

        <div className="mkt-student-row">
          <div>Name: <span className="mkt-underline" /></div>
          <div>Date: <span className="mkt-underline short" /></div>
        </div>

        <div className="mkt-instructions">
          <strong>How to submit:</strong> Complete each question on the worksheet PDF, then shade the matching
          bubble below with a dark pencil or pen. Snap a clear, well-lit photo of this filled sheet and tap
          <em> Upload photo</em> on the grading page (or scan the QR code above).
        </div>

        <div className="mkt-anchor-row">
          <span className="mkt-anchor" />
          <span className="mkt-anchor-title">ANSWER SHEET · SHADE ONE BUBBLE PER ROW</span>
          <span className="mkt-anchor" />
        </div>

        <table className="mkt-bubble-table">
          <thead>
            <tr>
              <th>Q</th><th>A</th><th>B</th><th>C</th><th>D</th>
              <th className="mkt-gap" />
              <th>Q</th><th>A</th><th>B</th><th>C</th><th>D</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ left, right }) => (
              <tr key={left}>
                <td className="mkt-q-num-cell">{left}</td>
                {letters.map((L) => (
                  <td key={`L${L}`}><span className="mkt-bubble">{L}</span></td>
                ))}
                <td className="mkt-gap" />
                {right <= total ? (
                  <>
                    <td className="mkt-q-num-cell">{right}</td>
                    {letters.map((L) => (
                      <td key={`R${L}`}><span className="mkt-bubble">{L}</span></td>
                    ))}
                  </>
                ) : (
                  <>
                    <td /><td /><td /><td /><td />
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mkt-anchor-row">
          <span className="mkt-anchor" />
          <span className="mkt-anchor-title">END OF ANSWER SHEET</span>
          <span className="mkt-anchor" />
        </div>

        <div className="mkt-footer">
          Purchase ID: {purchase.id.slice(0, 8)} · Auto-graded via prof-scaly.com
        </div>
      </div>

      <style>{`
        .mkt-print-root { background:#f8fafc; min-height:100vh; padding:24px 12px 60px; color:#0f172a; }
        .mkt-print-toolbar { max-width:820px; margin:0 auto 16px; display:flex; justify-content:space-between; align-items:center; }
        .mkt-sheet { max-width:820px; margin:0 auto; background:#fff; padding:36px 48px; border-radius:14px; box-shadow:0 8px 24px rgba(15,23,42,.06); font-family:'Georgia','Cambria',serif; }
        .mkt-header { display:flex; justify-content:space-between; align-items:flex-start; gap:20px; border-bottom:2px solid #92400e; padding-bottom:14px; margin-bottom:16px; }
        .mkt-brand-tag { font-size:11px; text-transform:uppercase; letter-spacing:2px; color:#b45309; font-weight:700; }
        .mkt-title { font-size:22px; font-weight:700; color:#0f172a; margin-top:6px; }
        .mkt-sub { font-size:13px; color:#475569; margin-top:2px; }
        .mkt-qr { text-align:center; }
        .mkt-qr-caption { font-size:10px; color:#64748b; margin-top:4px; letter-spacing:.5px; text-transform:uppercase; }
        .mkt-student-row { display:flex; gap:24px; margin:16px 0; font-size:14px; }
        .mkt-underline { display:inline-block; border-bottom:1.5px solid #0f172a; width:200px; height:16px; vertical-align:bottom; margin-left:6px; }
        .mkt-underline.short { width:120px; }
        .mkt-instructions { background:#fef3c7; border-left:4px solid #d97706; padding:10px 14px; font-size:13px; color:#78350f; border-radius:4px; margin-bottom:14px; }
        .mkt-anchor-row { display:flex; align-items:center; gap:10px; margin:10px 0; }
        .mkt-anchor { width:14px; height:14px; background:#0f172a; border-radius:2px; }
        .mkt-anchor-title { flex:1; text-align:center; font-size:11px; letter-spacing:3px; font-weight:700; color:#0f172a; }
        .mkt-bubble-table { width:100%; border-collapse:separate; border-spacing:0 6px; margin:8px 0; }
        .mkt-bubble-table th { font-size:11px; color:#64748b; padding:4px; text-align:center; text-transform:uppercase; letter-spacing:1px; }
        .mkt-bubble-table td { text-align:center; padding:2px; }
        .mkt-gap { width:16px; }
        .mkt-q-num-cell { font-weight:700; width:36px; color:#0f172a; }
        .mkt-bubble { display:inline-flex; align-items:center; justify-content:center; width:26px; height:26px; border:1.6px solid #0f172a; border-radius:50%; font-size:11px; font-weight:600; color:#0f172a; }
        .mkt-footer { text-align:center; font-size:10px; color:#64748b; margin-top:20px; letter-spacing:.5px; text-transform:uppercase; }
        @media print {
          .no-print { display:none !important; }
          .mkt-print-root { background:#fff; padding:0; }
          .mkt-sheet { box-shadow:none; margin:0; padding:24px 36px; border-radius:0; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
    </div>
  );
};

export default MarketplacePrintPage;
